import React from 'react';
import { Popover } from 'antd';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { useStore } from 'zustand';

import { isHasAccess } from 'features/validateAccess';
import { CalendarEvent, EventId, calendarStore } from 'entities/calendar';

import { UPDATE_CALENDAR } from '../../CalendarsLayout.permissions';
import { isWeekday } from '../../lib/isWeekday';
import { CalendarPopoverContent } from '../CalendarPopoverContent';

import styles from './MonthDayCell.module.scss';

type MonthDayCellProps = {
  day: Dayjs;
  event?: CalendarEvent;
  isOpened: boolean;
  openedEventId: EventId | null;
  onSelect: (date: Dayjs) => void;
  isBlocked: boolean;
  isSelected: boolean;
};

const MonthDayCell = React.forwardRef<HTMLDivElement, MonthDayCellProps>(
  ({ day, event, isOpened, openedEventId, onSelect, isBlocked, isSelected }, ref) => {
    const isCurrentDay = day.isSame(dayjs(), 'day');
    const setOpenedEventId = useStore(calendarStore, (state) => state.setOpenedEventId);
    const formattedDate = day.format('YYYY-MM-DD');

    const handleEventOpenChange = (open: boolean, eventDates: EventId): void => {
      setOpenedEventId(open ? eventDates : null);
    };

    const canUpdateCalendarEvent = isHasAccess(UPDATE_CALENDAR);

    if (!event) {
      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className={classNames(styles.monthDaysItem, {
            [styles.weekendDay]: isWeekday(day),
            [styles.currentDay]: isCurrentDay,
            [styles.blockedDay]: isBlocked,
            [styles.selectedDay]: isSelected,
          })}
          onClick={() => {
            if (!canUpdateCalendarEvent) return;
            onSelect(day);
          }}
        >
          {day.format('D')}
        </div>
      );
    }

    const showPopup = isOpened && openedEventId?.clickedDate === formattedDate;

    return (
      <Popover
        content={<CalendarPopoverContent event={event} />}
        trigger="click"
        open={showPopup}
        onOpenChange={(open: boolean): void => {
          if (isBlocked) return;
          handleEventOpenChange(open, {
            startDate: event.startDate,
            endDate: event.endDate,
            clickedDate: formattedDate,
          });
        }}
      >
        <div
          ref={ref}
          className={classNames(styles.monthDaysItem, styles[event.exclusionType.toLowerCase()], {
            [styles.weekendDay]: isWeekday(day),
            [styles.currentDay]: isCurrentDay,
            [styles.blockedDay]: isBlocked,
            [styles.selectedDay]: isOpened,
          })}
        >
          {day.format('D')}
        </div>
      </Popover>
    );
  },
);

export default React.memo(MonthDayCell, (prevProps, nextProps) => {
  return prevProps.isOpened === nextProps.isOpened;
});
